import Image from 'next/image'
import {FC} from 'react'

import {Text, TextVariant} from '@/components/common/content/Text'
import {
  resourcesSubmenuItemContent,
  ResourceSubmenuItem,
  SubmenuProps,
  useHeaderContent,
} from '@/content/header'
import {useTranslation} from '@/i18n'
import {theme} from '@/theme/theme'
import {
  MOBILE_SCREENSIZE,
  TABLET_SCREENSIZE,
  USER_TABLET_SCREENSIZE,
} from '@/utils/styles/breakpoints'
import {analytics} from '@/utils/tracking/analytics'

import {SubmenuItem} from './SubmenuItem'

type ResourcesSubmenuProps = SubmenuProps & {items?: ResourceSubmenuItem[]}

export const ResourcesSubmenu: FC<ResourcesSubmenuProps> = ({closeNavigation, items}) => {
  const {t} = useTranslation('page')
  const {
    resourcesIntro,
    menu: {
      navigation: {resources},
    },
  } = useHeaderContent()

  const submenuItems = (items ?? resources.submenu.defaultItems).map(
    (item) => resourcesSubmenuItemContent[item],
  )

  return (
    <>
      <div className="intro">
        <div className="image">
          <Image
            src={resourcesIntro.imageSrc}
            alt={resourcesIntro.imageAlt}
            style={{objectFit: 'contain'}}
            fill
            sizes="125px"
          />
        </div>
        <div className="desktop-title">
          <Text variant={TextVariant.h5} color={theme.palette.text.white}>
            {t(resourcesIntro.title)}
          </Text>
        </div>
        <div className="description">
          <Text color={theme.palette.gray[300]}>{t(resourcesIntro.description)}</Text>
        </div>
      </div>
      <ul className="list">
        {submenuItems.map(({icon, title, description, linkHref, trackedEvent}) => (
          <li key={title}>
            <SubmenuItem
              icon={icon}
              title={{text: t(title)}}
              description={t(description)}
              linkHref={linkHref}
              onClick={() => {
                trackedEvent && analytics.track(trackedEvent)
                closeNavigation && closeNavigation()
              }}
              isHighlighted
            />
          </li>
        ))}
      </ul>

      <style jsx>{`
        .intro {
          width: 100%;
          max-width: 250px;
          margin-right: ${theme.spacing[5]};
        }
        .image {
          position: relative;
          width: 125px;
          height: 80px;
          margin-bottom: ${theme.spacing[4]};
        }
        .description {
          margin-top: ${theme.spacing[2]};
        }
        .list {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: ${theme.spacing[2]};
        }
        @media ${TABLET_SCREENSIZE} {
          .intro {
            max-width: inherit;
            margin-right: 0;
          }
          .list {
            margin-top: ${theme.spacing[4]};
          }
          .desktop-title {
            display: none;
          }
          .description {
            margin-top: ${theme.spacing[4]};
          }
        }
        @media ${USER_TABLET_SCREENSIZE} {
          .list {
            grid-template-columns: repeat(2, 1fr);
          }
        }
        @media ${MOBILE_SCREENSIZE} {
          .list {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </>
  )
}
